import React, { useEffect, useState } from 'react';
import axios from 'axios';
import './HeroSettings.css';

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000';

const emptyHero = {
  subtitle: '',
  headlineLine1: '',
  headlineLine2: '',
  description: '',
  subtitleFontSize: 16,
  headlineFontSize: 85,
  descriptionFontSize: 18,
};

function HeroSettings() {
  const [hero, setHero] = useState(emptyHero);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [message, setMessage] = useState('');

  useEffect(() => {
    const fetchHero = async () => {
      try {
        const response = await axios.get(`${API_BASE_URL}/api/hero`);
        setHero({ ...emptyHero, ...response.data });
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error(err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchHero();
  }, []);

  const handleChange = (field, value) => {
    setHero((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    setIsSaving(true);
    setMessage('');

    try {
      await axios.put(`${API_BASE_URL}/api/hero`, hero);
      setMessage('Hero content updated successfully.');
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error(err);
      setMessage('Unable to save hero content. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return <p className="hero-settings-loading">Loading hero content…</p>;
  }

  return (
    <div className="hero-settings">
      <div className="hero-settings-header">
        <div>
          <h2 className="hero-settings-title">Hero headline</h2>
          <p className="hero-settings-subtitle">
            Update the main hero text displayed on the home page. The background
            image and animations stay the same.
          </p>
        </div>
        <button
          type="button"
          className="hero-settings-save-btn"
          onClick={handleSave}
          disabled={isSaving}
        >
          {isSaving ? 'Saving…' : 'Save changes'}
        </button>
      </div>

      <div className="hero-settings-grid">
        <div className="hero-settings-field">
          <label className="hero-settings-label">
            Subtitle
            <input
              type="text"
              className="hero-settings-input"
              value={hero.subtitle}
              onChange={(e) => handleChange('subtitle', e.target.value)}
              placeholder="Distinguished Professional Services"
            />
          </label>
        </div>

        <div className="hero-settings-field">
          <label className="hero-settings-label">
            Headline – first line
            <input
              type="text"
              className="hero-settings-input"
              value={hero.headlineLine1}
              onChange={(e) => handleChange('headlineLine1', e.target.value)}
              placeholder="NAVIGATING COMPLEXITY"
            />
          </label>
        </div>

        <div className="hero-settings-field">
          <label className="hero-settings-label">
            Headline – second line
            <input
              type="text"
              className="hero-settings-input"
              value={hero.headlineLine2}
              onChange={(e) => handleChange('headlineLine2', e.target.value)}
              placeholder="WITH CONFIDENCE"
            />
          </label>
        </div>

        <div className="hero-settings-field hero-settings-field-wide">
          <label className="hero-settings-label">
            Description
            <textarea
              className="hero-settings-textarea"
              rows={4}
              value={hero.description}
              onChange={(e) => handleChange('description', e.target.value)}
              placeholder="Main descriptive paragraph under the hero headline."
            />
          </label>
        </div>

        <div className="hero-settings-field">
          <label className="hero-settings-label">
            Subtitle font size (px)
            <input
              type="number"
              className="hero-settings-input"
              min={10}
              max={40}
              value={hero.subtitleFontSize}
              onChange={(e) =>
                handleChange('subtitleFontSize', Number(e.target.value) || 0)
              }
            />
          </label>
        </div>

        <div className="hero-settings-field">
          <label className="hero-settings-label">
            Headline font size (px)
            <input
              type="number"
              className="hero-settings-input"
              min={30}
              max={120}
              value={hero.headlineFontSize}
              onChange={(e) =>
                handleChange('headlineFontSize', Number(e.target.value) || 0)
              }
            />
          </label>
        </div>

        <div className="hero-settings-field">
          <label className="hero-settings-label">
            Description font size (px)
            <input
              type="number"
              className="hero-settings-input"
              min={10}
              max={32}
              value={hero.descriptionFontSize}
              onChange={(e) =>
                handleChange('descriptionFontSize', Number(e.target.value) || 0)
              }
            />
          </label>
        </div>
      </div>

      {message && <p className="hero-settings-message">{message}</p>}
    </div>
  );
}

export default HeroSettings;

